import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { GameEvent } from "@/lib/apiSports";

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/api-sports`;

interface GameEventsResult {
  events: GameEvent[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Fetches game events (goals, penalties) for a specific game ID.
 * - Fetches on-demand when modal opens (enabled flag)
 * - Caches for 60 seconds
 * - Auto-refreshes every 30s for live games if autoRefresh is true (Pro/Elite only)
 */
export function useGameEvents(
  gameId: number | null,
  enabled: boolean,
  isLive: boolean,
  autoRefresh: boolean
): GameEventsResult {
  const { data, isLoading, error } = useQuery({
    queryKey: ["gameEvents", gameId],
    queryFn: async (): Promise<GameEvent[]> => {
      if (!gameId) return [];

      const { data: sessionData } = await supabase.auth.getSession();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (sessionData?.session?.access_token) {
        headers["Authorization"] = `Bearer ${sessionData.session.access_token}`;
      }

      const params = new URLSearchParams({
        endpoint: "/games/events",
        game: String(gameId),
      });

      const response = await fetch(`${FUNCTION_URL}?${params}`, { headers });
      if (!response.ok) return [];

      const json = await response.json();
      if (!json?.response || !Array.isArray(json.response)) return [];

      // Filter to goal events only
      return json.response.filter(
        (e: GameEvent) => e.type?.toLowerCase() === "goal"
      );
    },
    enabled: enabled && !!gameId && gameId > 0,
    staleTime: 60_000, // 60 second cache
    refetchInterval: isLive && autoRefresh ? 15_000 : false,
    retry: 1,
  });

  return {
    events: data ?? [],
    isLoading,
    error: error as Error | null,
  };
}
